import { isNil } from "lodash";
import PropTypes from "prop-types";
import React, { Fragment, PureComponent } from "react";

import dateFunctions, { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import dateByAddingSpan from "../../modules/definition/calendar/dateFunctions/dateByAddingSpan";
import { getIs2400 } from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import { Precision, PropTypeShapes } from "../../types";
import calendarTypes from "../../types/calendarTypes";
import { TimespanRecord } from "../../types/immutableRecords/model/data/TimespanRecord";
import CalendarPickerSelectorController from "./CalendarPickerSelectorController";
import CalendarPickerSelectorGrid from "./CalendarPickerSelectorGrid";
import CalendarPickerSelectorTime from "./CalendarPickerSelectorTime";
import { NUM_OF_YEAR_IN_GRID } from "./constants";
import { getPrecision, getPrecisionOfDate } from "./utils";

const getValidDate = (date, calendar) => {
  return isDateValid(date)
    ? date
    : dateFunctions.dateForToday(calendar).set("precision", date && date.precision ? date.precision : Precision.Year);
};

// TODO: Revisit this function as it creates a potentially invalid DateRecord
// (hour = 24 is not considered valid by our date calculations functions)
// Right now it is just a showDate and never gets out of this class, so it is
// safe-ish, but if anyone ever sends it out in a callback etc. then all bets are
// off, so it would be better not to create it in the first place.
const adjustEndDate = (calendar, date, startDate) => {
  return getIs2400(startDate, date, calendar.timeFormat)
    ? dateByAddingSpan(calendar, date, TimespanRecord({ seconds: -1 }))
      .merge({ hour: 24, minute: 0, second: 0, precision: date.precision })
      .update("timestamp", t => t + 1)
    : date;
};

const getIsYearInTheLargestGridOfEra = (year, duration) => {
  const durationMod = duration % NUM_OF_YEAR_IN_GRID;
  return duration - year < (durationMod > 0 ? durationMod : NUM_OF_YEAR_IN_GRID);
};

const isCrossingEraOnScrollGrid = (calendar, date, isNextGrid) => {
  let result = false;
  if (isDateValid(date)) {
    const { era, year } = date;
    const currentEra = calendar.eras.get(era);
    if (currentEra) {
      const { isBackwards, duration } = currentEra;
      if (year <= NUM_OF_YEAR_IN_GRID) {
        result = isBackwards ? isNextGrid : !isNextGrid;
      } else if (getIsYearInTheLargestGridOfEra(year, duration)) {
        result = isBackwards ? !isNextGrid : isNextGrid;
      }
    }
  }
  return result;
};

const shouldMoveSingleYearOnScrollGrid = (calendar, date, isNextGrid, isCrossingEra) => {
  let result = false;
  if (isDateValid(date)) {
    const isCrossing = isCrossingEra;
    if (isCrossing) {
      const { era } = date;
      const currentEra = calendar.eras.get(era);
      const { isBackwards } = currentEra;
      const goingEraIndex = era + (isNextGrid ? 1 : -1);
      if (goingEraIndex >= 0 && goingEraIndex < calendar.eras.size) {
        const isGoingBackwards = calendar.eras.get(goingEraIndex).isBackwards;
        result = (isBackwards && !isGoingBackwards && isNextGrid) || (!isBackwards && isGoingBackwards && !isNextGrid);
      }
    }
  }
  return result;
};

const getAdjustByCurrentEraDuration = (duration, date) => {
  const durationMod = duration % NUM_OF_YEAR_IN_GRID;
  return duration - date.year < NUM_OF_YEAR_IN_GRID && durationMod ? NUM_OF_YEAR_IN_GRID - durationMod : 0;
};

const getYearDiffOnScrollGrid = (calendar, date, isNext, isCrossingEra) => {
  const era = calendar.eras.get(date.era);
  const currentYear = date.year;
  const { duration, isBackwards } = era;
  let adjustByGoingEraDuration = 0;
  let adjustByCurrentEraDuration = 0;
  if (isCrossingEra) {
    const goingEra = calendar.eras.get(date.era + (isNext ? 1 : -1));
    const { duration: goingDuration, isBackwards: goingBackwards } = goingEra;
    // isNext && goingBackwards || !isNext && !goingBackwards
    if (isNext === goingBackwards) {
      const durationMod = goingDuration % NUM_OF_YEAR_IN_GRID;
      adjustByGoingEraDuration = durationMod ? NUM_OF_YEAR_IN_GRID - durationMod : 0;
    }

    adjustByCurrentEraDuration = getAdjustByCurrentEraDuration(duration, date);
  }

  let result =
    NUM_OF_YEAR_IN_GRID -
    ((!isBackwards && isNext) || (isBackwards && !isNext) ? 1 : -1) * ((currentYear - 1) % NUM_OF_YEAR_IN_GRID);

  result += (result > 0 ? -1 : 1) * adjustByCurrentEraDuration;
  result += (result > 0 ? -1 : 1) * adjustByGoingEraDuration;

  return result;
};

class CalendarPickerSelector extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      date: getValidDate(props.date, props.calendar),
      selectedPrecision: getPrecisionOfDate(props.date, props.calendar)
    };
    this.onSelectedPrecisionChange = this.onSelectedPrecisionChange.bind(this);
    this.onNextGrid = this.onNextGrid.bind(this);
    this.onPrevGrid = this.onPrevGrid.bind(this);
    this.onChangeFromGrid = this.onChangeFromGrid.bind(this);
  }

  componentDidUpdate(prevProps) {
    this.onDateChange(prevProps.date, this.props.date);
  }

  onDateChange(prevDate, newDate) {
    if (
      isNil(newDate) ||
      isNil(prevDate) ||
      prevDate.timestamp !== newDate.timestamp ||
      prevDate.precision !== newDate.precision
    ) {
      this.setState({
        date: getValidDate(newDate, this.props.calendar),
        selectedPrecision: getPrecisionOfDate(newDate, this.props.calendar)
      });
    }
  }

  onSelectedPrecisionChange(newPrecision) {
    const changePrecision = newPrecision;
    if (changePrecision !== this.state.selectedPrecision) {
      this.setState({
        selectedPrecision: changePrecision
      });
    } else {
      // jump back to the date precision
      this.setState({
        selectedPrecision: getPrecision(this.props.date.precision, this.props.calendar)
      });
    }
  }

  onScrollGrid(isNext = true) {
    const { selectedPrecision, date } = this.state;
    const { calendar } = this.props;
    const changeDirection = isNext ? 1 : -1;
    // update state date
    let diffObj = null;
    // Do not scroll when Precision.century
    if (selectedPrecision === Precision.Year) {
      // Adjust to the first year of the grid
      // When crossing era, need to calculate differently
      const isCrossingEra = isCrossingEraOnScrollGrid(calendar, date, isNext);
      const shouldMoveSingleYear = shouldMoveSingleYearOnScrollGrid(calendar, date, isNext, isCrossingEra);
      const yearsDiff = shouldMoveSingleYear
        ? changeDirection
        : changeDirection * getYearDiffOnScrollGrid(calendar, date, isNext, isCrossingEra);
      diffObj = { years: yearsDiff };
    } else if (selectedPrecision === Precision.Month) {
      diffObj = { years: changeDirection };
    } else if (selectedPrecision >= Precision.Day) {
      diffObj = { months: changeDirection };
    }
    if (diffObj) {
      this.setState({
        date: dateByAddingSpan(calendar, date, TimespanRecord(diffObj))
      });
    }
  }

  onNextGrid() {
    this.onScrollGrid(true);
  }

  onPrevGrid() {
    this.onScrollGrid(false);
  }

  onChangeFromGrid(newDate) {
    this.setState({ date: newDate });
    const { selectedPrecision } = this.state;
    const datePrecision = this.props.date.precision;
    if (selectedPrecision < Precision.Day && selectedPrecision < datePrecision) {
      this.setState({ selectedPrecision: datePrecision });
    } else if (isNil(datePrecision) || selectedPrecision >= Precision.Day || selectedPrecision === datePrecision) {
      this.props.onChange(newDate);
      this.setState({ date: getValidDate(this.props.date, this.props.calendar) });
    }
  }

  render() {
    const { calendar, onChange, isEndDate, startDate, date } = this.props;
    const showDate = this.state.date;
    const adjustedShowDate =
      isEndDate && dateFunctions.areDatesSame(date, showDate) ? adjustEndDate(calendar, showDate, startDate) : showDate;
    const adjustedDate = isEndDate ? adjustEndDate(calendar, date, startDate) : date;
    const calendarType = calendar.get("calendarType");

    return (
      <Fragment>
        {calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME ? (
          <Fragment>
            <CalendarPickerSelectorController
              date={adjustedShowDate}
              calendar={calendar}
              selectedPrecision={this.state.selectedPrecision}
              onSelectedPrecisionChange={this.onSelectedPrecisionChange}
              onNextGrid={this.onNextGrid}
              onPrevGrid={this.onPrevGrid}
            />
            <CalendarPickerSelectorGrid
              calendar={calendar}
              date={showDate}
              onChange={this.onChangeFromGrid}
              selectedPrecision={this.state.selectedPrecision}
              selectedDate={adjustedDate}
              // For iOS touch event
              //#!if iOS === true
// onNextGrid={this.onNextGrid}
// onPrevGrid={this.onPrevGrid}
              //#!endif
            />
            {(date && date.precision) >= Precision.Hour ? (
              <CalendarPickerSelectorTime calendar={calendar} date={adjustedShowDate} onChange={onChange} />
            ) : null}
          </Fragment>
        ) : null}
      </Fragment>
    );
  }
}

CalendarPickerSelector.propTypes = {
  calendar: PropTypeShapes.calendar.isRequired,
  date: PropTypeShapes.dateRelated,
  onChange: PropTypes.func,
  isEndDate: PropTypes.bool,
  startDate: PropTypeShapes.dateRelated
};

export default CalendarPickerSelector;
